import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery } from "@connectrpc/connect-query";
import { skipToken } from "@tanstack/react-query";
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, SearchIcon, SearchXIcon } from "lucide-react";

import { ErrorState } from "@/components/error-state";
import { EmptyState, PageHeader } from "@/components/page-header";
import { StateCountBar, StateCountLegend, sliceStateCounts } from "@/components/state-count-bar";
import { BatchStatusBadge, MessageStateBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { MessageState } from "@/gen/bmi/v1/batch_message_pb";
import { AdminService } from "@/gen/wabms/admin/v1/admin_pb";
import { isNotFound } from "@/lib/errors";
import {
  formatBoth,
  formatCount,
  formatRelative,
  messageStateLabel,
} from "@/lib/format";
import { usePager } from "@/lib/paging";
import { MessageDetailSheet } from "@/routes/batches/message-detail-sheet";

const PAGE_SIZE = 25;
const ALL = "__all__";

export function BatchDetailPage() {
  const { batchId } = useParams<{ batchId: string }>();

  const [stateFilter, setStateFilter] = useState<string>(ALL);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  // Only the id travels; the sheet fetches its own detail via GetMessage.
  const [selectedMessageId, setSelectedMessageId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const handle = window.setTimeout(() => setSearchQuery(search.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const pager = usePager(`${batchId ?? ""}|${stateFilter}|${searchQuery}`);

  const batchQuery = useQuery(AdminService.method.getBatch, batchId ? { batchId } : skipToken);
  const batch = batchQuery.data?.batch;
  const ownerAccountId = batchQuery.data?.accountId ?? "";

  const messagesQuery = useQuery(
    AdminService.method.listMessages,
    batchId
      ? {
          batchId,
          pageSize: PAGE_SIZE,
          pageToken: pager.token,
          states: stateFilter === ALL ? [] : [Number(stateFilter) as MessageState],
          search: searchQuery,
        }
      : skipToken,
  );

  const rows = messagesQuery.data?.messages ?? [];
  const nextPageToken = messagesQuery.data?.nextPageToken ?? "";

  // The owner's label needs a second call; GetBatch returns only the id.
  const accountsQuery = useQuery(AdminService.method.listAccounts, {});
  const ownerLabel = useMemo(
    () => accountsQuery.data?.accounts.find((a) => a.accountId === ownerAccountId)?.label,
    [accountsQuery.data?.accounts, ownerAccountId],
  );

  if (batchQuery.isError && !isNotFound(batchQuery.error)) {
    return (
      <ErrorState
        error={batchQuery.error}
        title="Could not load this batch"
        onRetry={() => void batchQuery.refetch()}
      />
    );
  }

  if (batchQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (!batch) {
    return (
      <EmptyState
        icon={<SearchXIcon />}
        title="Batch not found"
        description={`No batch with id ${batchId ?? "(none)"}.`}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/batches">Back to batches</Link>
          </Button>
        }
      />
    );
  }

  const presentStates = sliceStateCounts(batch.stateCounts).map((s) => s.state);

  return (
    <>
      <div className="space-y-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2 w-fit">
          <Link to="/batches">
            <ArrowLeftIcon /> Batches
          </Link>
        </Button>

        <PageHeader
          title={batch.reference ?? "(no reference)"}
          description={
            <>
              <code className="font-mono">{batch.batchId}</code>
              {ownerAccountId ? (
                <>
                  {" · "}
                  <Link to={`/accounts/${ownerAccountId}`} className="hover:underline">
                    {ownerLabel ?? ownerAccountId}
                  </Link>
                </>
              ) : null}
              {" · submitted "}
              {formatBoth(batch.createdAt)}
            </>
          }
          actions={<BatchStatusBadge status={batch.status} />}
        />
      </div>

      <Card>
        <CardContent className="space-y-3">
          <div className="flex items-baseline justify-between">
            <p className="text-sm font-medium">{formatCount(batch.messageCount)} messages</p>
            {batch.completedAt ? (
              <p className="text-xs text-muted-foreground">
                Completed {formatRelative(batch.completedAt)}
              </p>
            ) : null}
          </div>
          <StateCountBar counts={batch.stateCounts} total={batch.messageCount} />
          <StateCountLegend counts={batch.stateCounts} />
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <SearchIcon className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search destination or client_message_id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Any state" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any state</SelectItem>
            {presentStates.map((state) => (
              <SelectItem key={state} value={String(state)}>
                {messageStateLabel(state)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {messagesQuery.isError ? (
        <ErrorState
          error={messagesQuery.error}
          title="Could not load messages"
          onRetry={() => void messagesQuery.refetch()}
        />
      ) : messagesQuery.isPending ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<SearchXIcon />}
          title="No messages match"
          description="Clear the state filter or the search box."
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Destination</TableHead>
                  <TableHead>client_message_id</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead className="text-right">Attempts</TableHead>
                  <TableHead>State changed</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((m) => (
                  <TableRow
                    key={m.messageId}
                    className="cursor-pointer"
                    onClick={() => setSelectedMessageId(m.messageId)}
                  >
                    <TableCell className="font-mono text-xs">{m.destination?.id ?? "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {m.clientMessageId}
                    </TableCell>
                    <TableCell>
                      <MessageStateBadge state={m.state} />
                    </TableCell>
                    <TableCell className="tabular text-right text-xs">{m.attemptCount}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatRelative(m.stateChangedAt)}
                    </TableCell>
                    <TableCell className="max-w-64 truncate text-xs text-tone-danger">
                      {m.error?.description ?? ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {formatCount(rows.length)} of {formatCount(batch.messageCount)} messages on this page.
              ListMessages is cursor-paginated, so there is no page count until the last page is
              reached.
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon-sm"
                aria-label="Previous page"
                disabled={!pager.hasPrev}
                onClick={pager.goPrev}
              >
                <ChevronLeftIcon />
              </Button>
              <span className="tabular px-2 text-xs text-muted-foreground">
                page {pager.page + 1}
              </span>
              <Button
                variant="outline"
                size="icon-sm"
                aria-label="Next page"
                disabled={nextPageToken === ""}
                onClick={() => pager.goNext(nextPageToken)}
              >
                <ChevronRightIcon />
              </Button>
            </div>
          </div>
        </>
      )}

      <MessageDetailSheet
        messageId={selectedMessageId}
        onOpenChange={(open) => !open && setSelectedMessageId(undefined)}
      />
    </>
  );
}
